<template>
  <div class="py-5 md:px-5">
    <PageHeader class="container" title="Addresses">
      <!-- <router-link :to="{ name: 'AddressCreate' }" class="btn btn-primary">
        Add Address
      </router-link> -->
      <button class="btn btn-secondary" v-submitting="submitting" @click="syncAddresses">
        <fa :icon="['far', 'exchange']" class="mr-2" /> Sync Addresses
      </button>
    </PageHeader>

    <div class="container">
      <!-- List -->
      <div class="card">
        <!-- Header -->
        <div class="flex flex-col justify-between p-4 border-b md:flex-row lg:p-5">
          <div class="grid grid-cols-2 gap-3 md:flex">
            <!-- Search -->
            <fieldset class="relative col-span-2">
              <fa :icon="['far', 'search']" class="absolute top-0 left-0 m-2 text-gray-400 pointer-events-none" />
              <input type="text" class="py-1 pl-8 form-input" placeholder="Search..." v-model="filter.search" @keydown.enter="applyFilter">
            </fieldset>
            <!-- Sort -->
            <sort-dropdown :options="sortOptions" />
          </div>
        </div>

        <!-- Loading -->
        <Loading v-if="loading" class="p-6" />

        <!-- Empty -->
        <div v-else-if="addresses.meta.total < 1 && !$route.query.filter" class="max-w-xs py-12 mx-auto text-center">
          <div class="mb-2 text-lg font-bold">
            No addresses have been added yet.
          </div>
          <div class="text-gray-500">
            Addresses that are created for bookings and orders will show up here.
          </div>
          <!-- <router-link :to="{ name: 'AddressCreate' }" class="mt-4 btn btn-secondary">
            Add a Address
          </router-link> -->
        </div>

        <!-- No Results -->
        <div v-else-if="addresses.meta.total < 1 && $route.query.filter" class="max-w-xs py-12 mx-auto text-center">
          <div class="mb-2 text-lg font-bold">
            No addresses matching your filters.
          </div>
          <div class="text-gray-500">
            Try changing your filter settings, or clear the filter to see all addresses.
          </div>
          <button class="mt-4 btn btn-outline" @click="clearFilter">
            Clear Filters
          </button>
        </div>

        <!-- Results -->
        <div v-else class="divide-y">
          <div class="grid px-5 py-3 text-xs font-medium tracking-wide text-gray-500 uppercase bg-gray-50 gap-x-4 sm:grid-cols-2">
            <div>Address</div>
            <div>Contact</div>
          </div>
          <!-- Table -->
          <div v-for="address in addresses.data" :key="address.id">
            <router-link :to="{ name: 'AddressEdit', params: { id: address.id } }" class="grid items-center px-5 py-3 text-gray-800 gap-x-4 sm:grid-cols-2 hover:no-underline hover:bg-gray-100">
              <div>
                <div class="font-medium">
                  {{ address.name }}
                </div>
                <div class="text-gray-500">
                  {{ address.formatted_address }}
                </div>
              </div>
              <div>
                <div class="font-medium">
                  {{ address.contact_name }}
                </div>
                <div class="text-gray-500">
                  {{ address.contact_phone }}
                </div>
              </div>
            </router-link>
          </div>
          <!-- Pagination -->
          <Pagination class="rounded-b-lg" :total="addresses.meta.total" :per-page="addresses.meta.per_page" :current-page="addresses.meta.current_page" />
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import SortDropdown from '@/components/SortDropdown'

export default {
  name: 'AddressList',

  metaInfo: {
    title: 'Addresses'
  },

  components: {
    SortDropdown
  },

  async created () {
    await this.getAddresses()
  },

  computed: {
    ...mapGetters(['account'])
  },

  data () {
    return {
      submitting: false,
      loading: true,
      addresses: {},
      filter: {
        search: ''
      },
      sortOptions: [
        { label: `Created <span class="text-gray-500">(Newest First)</span>`, prop: 'created_at', dir: 'desc' },
        { label: `Created <span class="text-gray-500">(Oldest First)</span>`, prop: 'created_at', dir: 'asc' },
        { label: `Name <span class="text-gray-500">(A&rarr;Z)</span>`, prop: 'name', dir: 'asc' },
        { label: `Name <span class="text-gray-500">(Z&rarr;A)</span>`, prop: 'name', dir: 'desc' },
        { label: `Contact Name <span class="text-gray-500">(A&rarr;Z)</span>`, prop: 'contact_name', dir: 'asc' },
        { label: `Contact Name <span class="text-gray-500">(Z&rarr;A)</span>`, prop: 'contact_name', dir: 'desc' },
        { label: `Street Address <span class="text-gray-500">(A&rarr;Z)</span>`, prop: 'formatted_address', dir: 'asc' },
        { label: `Street Address <span class="text-gray-500">(Z&rarr;A)</span>`, prop: 'formatted_address', dir: 'desc' }
      ]
    }
  },

  methods: {
    async getAddresses () {
      try {
        this.loading = true
        const { data } = await this.$api.get(`account/${this.$route.params.account}/addresses`, {
          params: {
            page: this.$route.query.page || 1,
            sort: this.$route.query.sort || 'created_at',
            dir: this.$route.query.dir || 'desc',
            ...this.filter && { filter: this.filter }
          }
        })
        this.addresses = data.addresses
        this.loading = false
      } catch (error) {
        console.error(error)
        this.$toasted.error(error.response.data.message)
      }
    },
    
    async syncAddresses () {
      try {
        this.submitting = true
        await this.$api.get(`account/${this.$route.params.account}/sync/addresses`)
        this.getAddresses()
      } catch (error) {
        console.error(error)
        this.$toasted.error(error.response.data.message)
      } finally {
        this.submitting = false
      }
    },

    setFilter () {
      if (this.$route.query.filter) {
        this.filter = JSON.parse(this.$route.query.filter)
      }
    },

    applyFilter () {
      this.$router.push({ 
        query: { 
          ...this.$route.query, 
          filter: JSON.stringify(this.filter)
        } 
      })
    },

    clearFilter () {
      this.filter = {
        search: '',
        orderdate: { start: sub(new Date(), { months: 1 }), end: add(new Date(), { months: 1 }) }
      }
      this.$router.push({ 
        query: { 
          ...this.$route.query, 
          filter: undefined
        } 
      })
    }
  },

  watch: {
    '$route.query': {
      handler (newVal, oldVal) {
        this.getAddresses()
      },
      deep: true
    }
  }
}
</script>